"use client";

import * as React from "react";

import { cn } from "../../cn";

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange" | "value" | "type"> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

/**
 * O interruptor do web e do desktop.
 *
 * Era o `Switch` do Radix no `apps/web` e um `Interruptor` escrito à mão no
 * painel de IA do desktop — as mesmas classes nos dois. Subiu sem o Radix: os
 * usos do web são todos controlados (`checked` + `onCheckedChange`), nenhum
 * depende do `<input>` escondido que o Radix põe em formulário nem do modo não
 * controlado, e sem ele o pacote não ganha dependência nova. Os atributos que o
 * Radix emitia (`role="switch"`, `aria-checked`, `data-state`,
 * `data-disabled`) continuam, para quem estiliza ou testa por eles.
 *
 * A gêmea nativa (`switch.native.tsx`) tem as mesmas medidas e cores.
 */
export const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked = false, onCheckedChange, onClick, className, disabled, ...props },
  ref,
) {
  const estado = checked ? "checked" : "unchecked";

  return (
    <button
      {...props}
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      data-state={estado}
      data-disabled={disabled ? "" : undefined}
      disabled={disabled}
      onClick={(evento) => {
        onClick?.(evento);
        if (!evento.defaultPrevented) onCheckedChange?.(!checked);
      }}
      className={cn(
        "peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        className,
      )}
    >
      <span
        aria-hidden
        data-state={estado}
        className="pointer-events-none block h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0"
      />
    </button>
  );
});
